import {useId, useState} from "react";
import {useTranslation} from "react-i18next";

import {Label} from "@/components/ui/label";
import Select from "@/components/ui/select";
import {useLocalePickerEnabled} from "@/src/modules/core/hooks/use-locale-picker-enabled";
import {meService} from "@/src/modules/core/services/me";
import {useStoredMe} from "@/src/modules/core/store/me";
import {toast} from "@/src/modules/core/hooks/use-toast";
import type {AppLocale} from "@/src/modules/core/i18n";

type LanguagePickerProps = {
  className?: string;
};

export function LanguagePicker({className}: LanguagePickerProps) {
  const {t} = useTranslation();
  const me = useStoredMe();
  const localePickerEnabled = useLocalePickerEnabled();
  const [pending, setPending] = useState(false);
  const userLangId = useId();

  if (!me || !localePickerEnabled) return null;

  const userValue = me.locale_override == null ? "system" : me.locale_override;

  const userOptions = [
    {value: "system", label: t("language.system")},
    {value: "en", label: t("language.english")},
    {value: "es", label: t("language.spanish")},
  ];

  const handleUserChange = async (value: string) => {
    setPending(true);
    try {
      await meService.updateLocale(value === "system" ? null : (value as AppLocale));
    } catch {
      toast.error(t("language.update_failed"));
    } finally {
      setPending(false);
    }
  };

  return (
    <div className={className ?? "flex flex-col gap-3"}>
      <div className="flex flex-col gap-1.5">
        <Label htmlFor={userLangId}>{t("language.label")}</Label>
        <Select
          id={userLangId}
          value={userValue}
          onChange={handleUserChange}
          disabled={pending}
          options={userOptions}
          placeholder={t("language.label")}
        />
      </div>
    </div>
  );
}
